import { useCallback, useEffect, useState } from 'react';
import { SlidersHorizontalIcon, Gauge } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '@/lib/utils';
import {
  getAvailableModels,
  updateChatThreadSettings,
  type ChatModel,
  type ChatThreadSettings,
  type ReasoningEffort,
  type Verbosity,
} from '../../services/data.store';
import { FeatureFlag, useFeatureFlag } from '../../services/featureFlag.service';
import type { ThreadSettings } from './threadSettings';

const VERBOSITY_OPTIONS: Array<{ value: Verbosity; label: string }> = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const REASONING_OPTIONS: Array<{ value: ReasoningEffort; label: string }> = [
  { value: 'max', label: 'Max' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

/**
 * State + behavior for the advanced thread controls: LaunchDarkly gate,
 * Cmd/Ctrl+A hotkey, per-thread settings with persistence.
 *
 * `supported` lets a surface opt out entirely (e.g. EDA/onboarding flows that
 * reuse the Ellie wrapper) — the panel, hotkey, and concise mode stay off.
 */
export function useAdvancedThreadControls({
  threadId,
  defaultModel,
  defaultVerbosity,
  supported = true,
  controlsHidden = false,
}: {
  threadId: string;
  defaultModel: string;
  defaultVerbosity: Verbosity;
  supported?: boolean;
  // Keep the settings plumbing (load/apply/concise mode) but never show the
  // panel or bind the hotkey — embedded chat, where settings are pinned by
  // the embedding application and the end-user must not retune them.
  controlsHidden?: boolean;
}) {
  const flagEnabled = useFeatureFlag(FeatureFlag.ADVANCED_THREAD_CONTROLS, false);
  const controlsEnabled = flagEnabled && supported && !controlsHidden;
  const [showControls, setShowControls] = useState(false);
  const [settings, setSettings] = useState<ThreadSettings>({
    verbosity: defaultVerbosity,
    model: defaultModel,
    // 'high' matches the Messages API default for adaptive thinking; the
    // backend ignores it for models without adaptive thinking support
    reasoning: 'high',
  });

  // Cmd/Ctrl+A toggles the panel. Skipped while typing in an input/textarea so
  // select-all still works in the message box.
  useEffect(() => {
    if (!controlsEnabled) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'a' && (event.metaKey || event.ctrlKey)) {
        const target = event.target as HTMLElement | null;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
          return;
        }
        event.preventDefault();
        setShowControls((v) => !v);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [controlsEnabled]);

  // Initialize from settings persisted on the thread, once loaded
  const applyLoadedSettings = useCallback((loaded: ChatThreadSettings | null) => {
    if (!loaded) return;
    setSettings((prev) => ({
      verbosity: loaded.verbosity ?? prev.verbosity,
      model: loaded.model ?? prev.model,
      reasoning: loaded.reasoning ?? prev.reasoning,
    }));
  }, []);

  const handleSettingsChange = useCallback((next: ThreadSettings) => {
    setSettings(next);
    // Persist; 404s are expected for threads that don't exist yet (settings
    // also ride along on every completion request and persist at stream end)
    updateChatThreadSettings(threadId, {
      verbosity: next.verbosity,
      model: next.model,
      reasoning: next.reasoning,
    }).catch(() => {});
  }, [threadId]);

  return { controlsEnabled, showControls, settings, applyLoadedSettings, handleSettingsChange };
}

interface AdvancedThreadControlsProps {
  open: boolean;
  settings: ThreadSettings;
  onSettingsChange: (settings: ThreadSettings) => void;
  threadId: string;
}

/**
 * Slide-down panel with advanced per-thread controls (verbosity, model
 * picker). Hidden by default; toggled with Cmd/Ctrl+A when the
 * `advanced-thread-controls` LaunchDarkly flag is enabled.
 */
export const AdvancedThreadControls: React.FC<AdvancedThreadControlsProps> = ({
  open,
  settings,
  onSettingsChange,
  threadId,
}) => {
  const [models, setModels] = useState<ChatModel[]>([]);
  const meteringEnabled = useFeatureFlag(FeatureFlag.COST_BREAKDOWNS, false);

  useEffect(() => {
    if (!open || models.length > 0) return;
    getAvailableModels()
      .then(setModels)
      .catch((err) => console.warn('[AdvancedThreadControls] Failed to load models:', err));
  }, [open, models.length]);

  // Disable the reasoning select when the selected model is known not to
  // support adaptive thinking. Unknown models (list not loaded yet, or stale
  // persisted value) stay enabled — the backend ignores reasoning for them.
  const selectedModel = models.find((m) => m.id === settings.model);
  const reasoningDisabled = selectedModel ? !selectedModel.supportsReasoning : false;

  return (
    <div
      className={cn(
        'flex-shrink-0 overflow-hidden transition-all duration-200 ease-out border-border',
        open ? 'max-h-10 opacity-100 border-b' : 'max-h-0 opacity-0'
      )}
      aria-hidden={!open}
    >
      <div className="flex items-center gap-4 px-4 py-1 bg-muted/50">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <SlidersHorizontalIcon className="h-3 w-3" />
          <span className="text-[10px] font-medium uppercase tracking-wide">Advanced</span>
        </div>

        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-muted-foreground">Verbosity</Label>
          <Select
            value={settings.verbosity}
            onValueChange={(verbosity) => onSettingsChange({ ...settings, verbosity: verbosity as Verbosity })}
          >
            <SelectTrigger className="h-6 w-24 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VERBOSITY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-xs">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div
          className={cn('flex items-center gap-1.5', reasoningDisabled && 'opacity-50')}
          title={reasoningDisabled ? 'The selected model does not support adaptive thinking' : undefined}
        >
          <Label className="text-xs text-muted-foreground">Reasoning</Label>
          <Select
            value={settings.reasoning}
            onValueChange={(reasoning) => onSettingsChange({ ...settings, reasoning: reasoning as ReasoningEffort })}
            disabled={reasoningDisabled}
          >
            <SelectTrigger className="h-6 w-24 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REASONING_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-xs">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-muted-foreground">Model</Label>
          <Select
            value={settings.model}
            onValueChange={(model) => onSettingsChange({ ...settings, model })}
          >
            <SelectTrigger className="h-6 w-48 text-xs">
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {/* Always include the current model so the trigger shows it even if it's not in the fetched list */}
              {!models.some((m) => m.id === settings.model) && (
                <SelectItem value={settings.model} className="text-xs">{settings.model}</SelectItem>
              )}
              {models.map((m) => (
                <SelectItem key={m.id} value={m.id} className="text-xs">
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {meteringEnabled && threadId && (
          <Link
            to={`/usage-analytics/ellie-usage/threads/${threadId}`}
            className="ml-auto flex items-center text-muted-foreground hover:text-foreground"
            title="View metering for this thread"
          >
            <Gauge className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
    </div>
  );
};
